import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API_URL, formatCurrency, getTokenSubject, getWebSocketBaseUrl } from '../demoMode';
import { apiGetJson, extractArray } from '../apiClient';
import {
  applyLoungeFilters,
  countOnlinePeople,
  filtersFromSearchParams,
  filtersToSearchParams,
  isDefaultLoungeFilters,
  sortLoungePeople,
} from '../featureModels';
import FilterSheet, { FilterIcon } from './FilterSheet';
import ProfileCard from './ProfileCard';
import { PrimaryButton, ScreenShell, SecondaryButton } from './ScreenShared';

export function LoungeScreen() {
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  useEffect(() => {
    let active = true;

    const loadUsers = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (active) {
          setPeople([]);
          setLoadError('Please log in again to load active profiles.');
          setLoading(false);
        }
        return;
      }

      try {
        const result = await apiGetJson('/v1/users?page=1&items_per_page=100', { token });

        if (!result.ok) {
          if (result.status === 401) {
            localStorage.removeItem('token');
            throw new Error('Session expired. Please log in again.');
          }

          const attemptNotes = (result.attempts || [])
            .map((attempt) => `${attempt.url} -> ${attempt.status || 'network error'}`)
            .join(' | ');
          throw new Error(
            `Could not load profiles (HTTP ${result.status || 0}). Attempts: ${attemptNotes}`
          );
        }

        const rows = extractArray(result.payload);

        if (active) {
          setPeople(Array.isArray(rows) ? rows : []);
          setLoadError('');
        }
      } catch (err) {
        if (active) {
          setPeople([]);
          setLoadError(err instanceof Error ? err.message : 'Could not load active profiles.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const username = getTokenSubject(token || '');

    if (!token || !username) {
      return;
    }

    const wsBase = getWebSocketBaseUrl(API_URL);
    const wsUrl = `${wsBase}/ws/${encodeURIComponent(username)}?token=${encodeURIComponent(token)}`;
    let socket = null;
    let heartbeatTimer = null;
    let reconnectTimer = null;
    let isActive = true;

    const clearHeartbeat = () => {
      if (heartbeatTimer) {
        window.clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    };

    const connect = () => {
      if (!isActive) {
        return;
      }

      socket = new WebSocket(wsUrl);

      socket.onopen = () => {
        socket.send(JSON.stringify({ type: 'heartbeat', timestamp: Date.now() }));
        clearHeartbeat();
        heartbeatTimer = window.setInterval(() => {
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'heartbeat', timestamp: Date.now() }));
          }
        }, 15000);
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === 'presence-updated' && Array.isArray(payload.online_users)) {
            setOnlineUsers(new Set(payload.online_users));
          }
        } catch (_err) {
          // Ignore malformed WS messages from non-presence events.
        }
      };

      socket.onerror = () => {
        if (socket && socket.readyState <= WebSocket.OPEN) {
          socket.close();
        }
      };

      socket.onclose = () => {
        clearHeartbeat();
        if (!isActive) {
          return;
        }
        reconnectTimer = window.setTimeout(connect, 3000);
      };
    };

    connect();

    return () => {
      isActive = false;
      clearHeartbeat();
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
      }
      if (socket) {
        socket.close();
      }
    };
  }, []);

  const visiblePeople = useMemo(
    () =>
      sortLoungePeople(applyLoungeFilters(people, filters), onlineUsers, filter, 24),
    [people, filters, onlineUsers, filter]
  );

  const onlineCount = useMemo(
    () => countOnlinePeople(people, onlineUsers),
    [people, onlineUsers]
  );

  return (
    <ScreenShell>
      <div className="ic-lounge-head">
        <div>
          <h2>Tonight's Lounge</h2>
          <p>{onlineCount} online now</p>
        </div>
        <div className="ic-lounge-filter-row">
          <button type="button" className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All</button>
          <button type="button" className={filter === 'online' ? 'active' : ''} onClick={() => setFilter('online')}>Live</button>
          <button
            type="button"
            className="ic-lounge-filter-btn"
            onClick={() => setFilterSheetOpen(true)}
            aria-label="Open filters"
          >
            <FilterIcon />
            {!isDefaultLoungeFilters(filters) ? <span className="ic-filter-dot" /> : null}
          </button>
        </div>
      </div>

      <div className="figma-card-grid">
        {loading ? <p className="figma-sub">Loading people...</p> : null}
        {!loading && loadError ? <p className="figma-sub">{loadError}</p> : null}
        {!loading && !loadError && visiblePeople.length === 0 ? <p className="figma-sub">No active profiles yet.</p> : null}
        {visiblePeople.map((p) => (
          <ProfileCard
            key={p.id || p.username}
            profile={p}
            isOnline={onlineUsers.has(p.username)}
            onClick={() => navigate(`/lounge/peek/${p.username}`)}
          />
        ))}
      </div>

      <div className="ic-lounge-foot-pill">
        <p>{onlineCount > 0 ? `${onlineCount} people are currently live` : 'Waiting for members to come online'}</p>
      </div>

      <FilterSheet
        open={filterSheetOpen}
        people={people}
        filters={filters}
        onApply={(nextFilters) => {
          setSearchParams(filtersToSearchParams(nextFilters), { replace: true });
          setFilterSheetOpen(false);
        }}
        onClose={() => setFilterSheetOpen(false)}
      />
    </ScreenShell>
  );
}

export function ProfilePeekScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);

  useEffect(() => {
    let active = true;

    const loadUser = async () => {
      const token = localStorage.getItem('token');
      if (!token || !id) {
        return;
      }

      try {
        const response = await fetch(`${API_URL}/v1/user/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await response.json().catch(() => null);
        if (active && response.ok && payload?.username) {
          setPerson(payload);
        }
      } catch (_err) {
        if (active) {
          setPerson(null);
        }
      }
    };

    loadUser();

    return () => {
      active = false;
    };
  }, [id]);

  const safePerson = person || { username: id || 'user', name: 'User', tier_id: null, rate_per_hour: null };

  return (
    <ScreenShell
      titleBar={
        <button
          type="button"
          className="figma-toplink"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      }
    >
      <div className="ic-profile-peek-hero">
        <div className="figma-avatar large" />
      </div>
      <h2 className="figma-section-title">{safePerson.name || safePerson.username}</h2>
      <p className="figma-sub">
        @{safePerson.username} • Tier {safePerson.tier_id || 'N/A'} • {safePerson.rate_per_hour ? `${formatCurrency(safePerson.rate_per_hour)}/hr` : 'Rate not set'}
      </p>
      <SecondaryButton to={`/chats/${safePerson.username}`}>Message</SecondaryButton>
      <PrimaryButton to={`/invite-confirm/${safePerson.username}`}>Start call</PrimaryButton>
    </ScreenShell>
  );
}

export function InvitesScreen() {
  const navigate = useNavigate();

  return (
    <ScreenShell titleBar={<span className="figma-toptext">Invites</span>}>
      <p className="figma-sub">Invite inbox was removed from the prototype flow.</p>
      <PrimaryButton onClick={() => navigate(-1)}>Back to lounge</PrimaryButton>
    </ScreenShell>
  );
}

export function InviteConfirmScreen() {
  const navigate = useNavigate();
  const { id } = useParams();

  return (
    <ScreenShell>
      <h2 className="figma-section-title">Start call with @{id}?</h2>
      <p className="figma-sub">Real-time call starts when the other person joins.</p>
      <div className="figma-grid-two">
        <SecondaryButton onClick={() => navigate(-1)}>Cancel</SecondaryButton>
        <PrimaryButton to={`/call/${id}`}>Start call</PrimaryButton>
      </div>
    </ScreenShell>
  );
}

export function CallScreen() {
  const navigate = useNavigate();
  const { id } = useParams();

  return (
    <ScreenShell titleBar={<span className="figma-toptext">Live Call • {id}</span>}>
      <div className="figma-video-stage">
        <div className="figma-video-tile">You</div>
        <div className="figma-video-tile">Guest</div>
      </div>
      <div className="figma-grid-two">
        <button className="figma-btn figma-btn-secondary" onClick={() => navigate(`/post-call/${id}`)}>End call</button>
        <button className="figma-btn figma-btn-primary" onClick={() => navigate(`/match/${id}`)}>It is a match</button>
      </div>
    </ScreenShell>
  );
}

export function PostCallScreen() {
  const { id } = useParams();

  return (
    <ScreenShell>
      <h2 className="figma-section-title">How was that call?</h2>
      <p className="figma-sub">Your feedback helps keep the lounge healthy.</p>
      <div className="figma-rate-stars">★★★★★</div>
      <PrimaryButton to={`/match/${id}`}>Submit</PrimaryButton>
    </ScreenShell>
  );
}

export function MatchScreen() {
  const navigate = useNavigate();
  const { id } = useParams();

  return (
    <ScreenShell>
      <h2 className="figma-title">It's a match</h2>
      <p className="figma-sub">You and {id} want to continue talking.</p>
      <div className="figma-grid-two">
        <SecondaryButton onClick={() => navigate(-1)}>Back to lounge</SecondaryButton>
        <PrimaryButton to={`/call/${id}`}>Call again</PrimaryButton>
      </div>
    </ScreenShell>
  );
}
